import {IFetchedListData, IDataLoadingState} from 'powtoon-commons/reduxThunkUtils/fetched-data'
import {IFolder, TagGroup} from 'powtoon-services/workspace/types'
import {IFetchedData} from 'powtoon-commons/reduxThunkUtils/fetched-data/types'
import {WithFilePermissions} from 'powtoon-services/permissions/types'
import {UserId} from 'powtoon-services/current-user'
import {DocumentContent} from 'powtoon-document/types'

export type PowtoonId = string
export type PowtoonType = 'recording' | 'studio'
export enum POWTOON_EXPORT_STATUS {
  PROCESSING = 'processing',
  READY = 'ready',
  ERROR = 'error'
}

export enum TagType {
  Translation = 'Translation',
  Locale = 'Locale'
}

export type powtoonTags = Record<TagType, string[]>

export interface Powtoon extends WithFilePermissions {
  id: PowtoonId,
  name: string,
  description: string,
  tags: powtoonTags,
  isPublished: boolean,
  publishUrl: string,
  externalUrl: string,
  videoUrl: string,
  captionsUrl?: string,
  thumb: string,
  lastModified: string,
  minRequire: string,
  currentVersion: string,
  category?: string,
  thumbUrl: string,
  isDynamic: boolean,
  consumedExport: boolean,
  contentSource?: string,
  collaborateInvite?: CollaborationInvitation,
  enableRichText?: boolean,
  type: PowtoonType,
  expiryDate?: string,
  duration: number,
  isLocked?: boolean,
  isShared?: boolean,
  itemType?: 'powtoon',
  createdAt?: string,
  translationStatus?: string,
  owner?: PowtoonOwner,
  accessLevel?: string[],
  content?: DocumentContent
}

export interface PowtoonWithContent extends Powtoon {
  content: DocumentContent
}

export interface PowtoonOwner {
  id: UserId,
  email: string,
  firstName: string,
  lastName: string
}

export interface SharedWith {
  count: number,
  results: SharedInfoData[],
  next?: number,
  previous?: number
}

export interface SharedWithState {
  [key: string]: IFetchedData<SharedWith> | undefined
}

export interface AccessPermissionsState {
  [key: string]: IFetchedData<string[]> | undefined
}

export interface ClosedCaptionsLocales {
  default: {code: string, name: string} | null,
  locales: {code: string, name: string}[]
}

export interface PowtoonsState {
  myPowtoons: IFetchedListData<Powtoon>,
  publishData: PublishDataMap,
  imports: ImportPowtoonReducer,
  exports: ExportPowtoonsState,
  collaborations: CollaborationsReducer,
  accessInfo: AccessInfoState,
  sharedWith: SharedWithState,
  accessPermissions: AccessPermissionsState,
  closedCaptionsLocales: IFetchedData<ClosedCaptionsLocales>,
  tags?: IFetchedListData<TagGroup> | undefined
}

export interface PublishTarget {
  name: string,
  displayName: string,
  category: string,
  mode: string,
  isAvailable: boolean
}

export interface PublishDataMap {
  [powtoonId: string]: PublishData | undefined
}

export interface PPTXImport {
  uploadProgress: number,
  generatingProgress: number,
  uploaded: boolean,
  terminated: boolean,
  error: string | object | null
}

export interface ImportPowtoonReducer {
  pptx: {
    [id: string]: PPTXImport
  }
}

export interface ExportsDataApiResponse {
  id: number,
  modified: string,
  target: 'powtoon' | 'mp4',
  version: string,
  status: POWTOON_EXPORT_STATUS,
}

export interface ExportPowtoonsState {
  [powtoonId: string]: {
    id: number,
    modified: string,
    target: 'powtoon' | 'mp4',
    version: string,
    status: POWTOON_EXPORT_STATUS,
    runDownloadAfterExport?: boolean
  }[]
}

export interface CollaborationsReducer {
  sendingProcess: IDataLoadingState,
  hideCollaborationNotificationDate: string,
  blinkedPowtoons: {
    [key: string]: boolean
  },
  publicLink?: string
}

export interface APIResponse<T> {
  count: number,
  next?: string,
  previous?: string,
  results: T[]
}

export interface PowtoonsMapExtension {
  'powtoon@@powtoons'?: PowtoonsState
}

export type PowtoonsAPIResponse = APIResponse<Powtoon>

export type CollaborationAPIResponse = APIResponse<CollaborationInvitation>

export interface CollaborationInvitation {
  id: string,
  name: string,
  description: string,
  powtoonId: string | null,
  collaboratedBy: {
    firstName: string,
    lastName: string
  },
  createdDate: string,
  acceptedDate: string | null,
  acknowledgedDate: string | null
}

export interface PowtoonsMapExtension {
  'powtoon@@powtoons'?: PowtoonsState
}
// tslint:disable-next-line:no-empty-interface
export interface DeletePowtoonAPIResponse {
}

export type VideoQuality = 720 | 1080
export interface PublishDataPermissions {
  allowedPublishTargets: string[],
  duration: number,
  outro: string,
  privacy: string,
  quality: VideoQuality,
  waterMark: string
}

export enum CaptionsMode {
  NONE = 'none',
  BURNED = 'burned',
  FILE = 'file'
}

export interface PublishDataUserLicense {
  exportCreditsBalance: number,
  isExportConsumed: boolean,
  isLimited: boolean,
  licenseId: number,
  numOfExportsPerCycle: number,
  renewalDate: string | null,
  includesPremiumAssets: boolean,
  requiredLicenseLevel: 'Free' | 'Professional' | 'ProfessionalPlus',
  publishAllowed: boolean
}

export interface PublishDataAPIResponse {
  currentPermissions: PublishDataPermissions,
  exportCreditPermissions: PublishDataPermissions,
  publishTargets: PublishTarget[],
  userLicense: PublishDataUserLicense
}

export interface PublishData extends PublishDataAPIResponse {
  availableExports: PublishTarget[],
  publishing: boolean
}

export interface PublishVideoArgs {
  target: string,
  aspect_ratio: string,
  rich_text: boolean,
  quality: 720 | 1080,
  outro: boolean,
  watermark: boolean,
  caption_locales?: string[],
  target_props: {
    mode: 'movie' | 'presentation',
    indexed: boolean,
    privacy?: boolean
  }
}

export function isPowtoon(item: Powtoon | IFolder): item is Powtoon {
  return item.itemType === 'powtoon'
}

export interface TranslatePowtoonParams {
  allow_empty_fields?: 'true' | 'false',
  force_version?: 'current_version' | 'original_version'
}

export interface SharedWithData {
  createdBy: UserId,
  creationDate: string,
  id: string,
  parentGroupId: string,
  thumbUrl: string | null,
  title: string,
  userCount: number
}

export interface SharedData {
  shareDate: string,
  sharedBy: {
    id: UserId,
    firstName: string,
    lastName: string,
    email: string
  },
  sharedWith: SharedWithData
}

export interface SharedInfoData extends WithFilePermissions {
  grantee: {
    email: string,
    firstName?: string,
    lastName?: string,
    id: string,
    provider: string,
    type: 'user' | 'lead',
    thumbUrl: null | string
  },
  granter: {
    email: string,
    firstName: string,
    id: UserId,
    lastName: string,
  },
  id: string,
  itemId: string,
  itemTypeName: 'powtoon',
  createdDate: string,
  lastModifiedDate: string
}

export enum UtmCampaign {
  WorkspaceShare = 'workspace_share',
  StudioShare = 'studio_share',
  PlayerPageShare = 'player_page_share',
  WorkspacePublish = 'workspace_publish',
  StudioPublish = 'studio_publish'
}

export interface AccessInfoState  {
  [key: string]: IFetchedData<SharedData[]> | undefined
}

export enum SharingPermissions {
  Comment = 'comment',
  Edit = 'edit',
  View = 'view'
}
